import React, { createContext, useContext, useState, type ReactNode } from 'react';

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('pt');

  const setLanguage = (lang: string) => {
    setCurrentLanguage(lang);
  };

  const t = (key: string): string => {
    return translations[currentLanguage]?.[key] || translations['pt'][key] || key;
  };
  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
// Traduções
const translations: { [key: string]: { [key: string]: string } } = {
  pt: {
    // Header
    'nav.home': 'Início',
    'nav.about': 'Sobre',
    'nav.skills': 'Skills',
    'nav.projects': 'Projetos',
    'nav.experience': 'Experiência',
    'nav.certifications': 'Certificados',
    'nav.contact': 'Contacto',

    // Hero
    'hero.greeting': 'Olá, eu sou',
    'hero.title': 'Fullstack Developer',
    'hero.description': 'Especialista em desenvolvimento web e mobile com experiência em liderança de equipas. Criador de soluções inovadoras para contact centers e gestão empresarial.',
    'hero.fullstack': 'Full Stack',
    'hero.mobile': 'Mobile',
    'hero.devops': 'DevOps',
    'hero.viewProjects': 'Ver Projetos',
    'hero.contact': 'Contactar',

    // About
    'about.title': 'Sobre',
    'about.subtitle': 'Conheça mais sobre minha trajetória e experiência profissional',
    'about.name': 'Nome',
    'about.location': 'Localização',
    'about.experience': 'Experiência',
    'about.certification': 'Certificação',
    'about.degree': 'Licenciatura',
    'about.nameValue': 'Karim Hussen Patatas Hassam dos Santos',
    'about.locationValue': 'Golães, Fafe, Portugal',
    'about.experienceValue': '4+ anos em desenvolvimento',
    'about.certificationValue': 'Oracle Certified Professional',
    'about.degreeValue': 'Licenciado em Informática',
    'about.currentRole': 'Cargo Atual',
    'about.currentRoleValue': 'Fullstack Developer na Randstad Digital (Setor Financeiro e Seguradora — Société Générale Assurances) — desde Agosto, GraphTalk AIA',
    'about.presentationTitle': 'Apresentação',
    'about.presentation1': 'Engenheiro de Software e Desenvolvedor Web/Mobile com sólida experiência em programação, gestão de equipas e desenvolvimento de soluções inovadoras para contact centers e gestão empresarial.',
    'about.presentation2': 'Especialista em React Native e Computação Móvel, com domínio avançado de Linux, Django, desenvolvimento web full stack, redes e integração de sistemas.',
    'about.presentation3': 'Reconhecido pela criatividade, capacidade de liderança e entrega de projetos de alto impacto. Busco uma posição desafiante na Europa para contribuir com soluções tecnológicas de ponta e crescer em ambientes internacionais dinâmicos.',
    'about.fullStackDev': 'Full Stack Developer',
    'about.fullStackDesc': 'Especialista em React, Django, React Native e desenvolvimento mobile',
    'about.ceoLeader': 'CEO & Líder Técnico',
    'about.ceoDesc': 'Gestão de equipas até 50 colaboradores e desenvolvimento de soluções empresariais',
    'about.techInnovation': 'Inovação Tecnológica',
    'about.techDesc': 'Criação de plataformas integradas para contact centers e gestão empresarial',

    // Skills
    'skills.title': 'Skills',
    'skills.subtitle': 'Tecnologias e ferramentas que domino para criar soluções completas',
    'skills.frontend': 'Frontend',
    'skills.backend': 'Backend',
    'skills.mobile': 'Mobile',
    'skills.database': 'Database',
    'skills.tools': 'Tools & DevOps',
    'skills.languages': 'Idiomas',
    'skills.proficiencyLevels': 'Níveis de Proficiência',
    'skills.native': 'Nativo: Língua materna',
    'skills.c2': 'C2: Proficiência plena',
    'skills.b2': 'B2: Utilizador independente',
    'skills.a1': 'A1: Utilizador elementar',

    // Projects
    'projects.title': 'Projetos',
    'projects.subtitle': 'Uma seleção dos meus trabalhos mais recentes e impactantes',
    'projects.all': 'Todos',
    'projects.web': 'Web',
    'projects.mobile': 'Mobile',
    'projects.fullstack': 'Full Stack',
    'projects.games': 'Jogos',
    'projects.viewSite': 'Ver Site',
    'projects.code': 'Código',
    'projects.online': 'Online',
    'projects.development': 'Em Desenvolvimento',
    'projects.completed': 'Concluído',
    'projects.testing': 'Em Testes',
    'projects.mobileApps': 'Apps Mobile em Teste',
    'projects.memoriza': 'App para técnicas de memorização',
    'projects.pocketcv': 'Criação de currículos profissionais',

    // Experience
    'experience.title': 'Experiência',
    'experience.subtitle': 'Trajetória profissional e formação acadêmica',
    'experience.professional': 'Experiência Profissional',
    'experience.education': 'Formação',
    'experience.current': 'Atual',
    'experience.technologies': 'Tecnologias:',
    'experience.achievements': 'Principais Conquistas:',
    'experience.careerSummary': 'Resumo da Carreira',
    'experience.totalExperience': 'Experiência Total:',
    'experience.teamManaged': 'Equipa Gerida:',
    'experience.projectsLed': 'Projetos Liderados:',
    'experience.certifications': 'Certificações:',
    'experience.years': '10+ anos',
    'experience.people': '50+ pessoas',
    'experience.projects': '15+',
    'experience.oracle': 'Oracle OCP',
    'experience.inProgress': 'Em curso',
    'experience.completed': 'Concluído',

    // Certifications
    'certifications.title': 'Certificações',
    'certifications.subtitle': 'certificações profissionais verificadas digitalmente',
    'certifications.view': 'Ver',
    'certifications.rumos': 'Certificações Rumos',
    'certifications.cisco': 'Certificações Cisco',
    'certifications.coursera': 'Certificações Coursera',

    // Contact
    'contact.title': 'Contacto',
    'contact.subtitle': 'Vamos trabalhar juntos! Entre em contacto para discutir oportunidades e projetos',
    'contact.info': 'Informações de Contacto',
    'contact.email': 'Email',
    'contact.location': 'Localização',
    'contact.availability': 'Disponibilidade',
    'contact.availabilityValue': 'Segunda a Sexta, 9h-18h',
    'contact.connectWithMe': 'Conecte-se Comigo',
    'contact.availableForProjects': 'Disponível para Projetos',
    'contact.availableDesc': 'Atualmente aberto a novas oportunidades em desenvolvimento full-stack, liderança técnica e consultoria em tecnologia.',
    'contact.sendMessage': 'Enviar Mensagem',
    'contact.name': 'Nome',
    'contact.subject': 'Assunto',
    'contact.message': 'Mensagem',
    'contact.namePlaceholder': 'Seu nome',
    'contact.emailPlaceholder': 'seu@email.com',
    'contact.subjectPlaceholder': 'Assunto da mensagem',
    'contact.messagePlaceholder': 'Descreva seu projeto ou oportunidade...',
    'contact.sending': 'Enviando...',
    'contact.send': 'Enviar Mensagem',
    'contact.responseTime': 'Respondo normalmente em',
    'contact.hours': '24 horas',

    // Git Activity
    'git.title': 'GitActivity',
    'git.subtitle': 'Atividade de desenvolvimento e contribuições no GitHub',
    'git.totalCommits': 'Total Commits',
    'git.repositories': 'Repositórios',
    'git.contributions2024': 'Contribuições 2024',
    'git.activeDays': 'Dias Ativos',
    'git.contributionsIn': 'Contribuições em 2024',
    'git.contributions': 'contribuições',
    'git.days': 'dias',
    'git.recentCommits': 'Commits Recentes',
    'git.viewOnGithub': 'Ver no GitHub',
    'git.less': 'Menos',
    'git.more': 'Mais',

    // Language names
    'lang.portuguese': 'Português',
    'lang.french': 'Francês',
    'lang.english': 'Inglês',
    'lang.spanish': 'Espanhol',
    'lang.chinese': 'Chinês',
    'lang.japanese': 'Japonês',
    'lang.thai': 'Tailandês',
    'lang.native': 'Nativo',
    'lang.portugal': 'Portugal',
    'lang.france': 'França',
    'lang.uk': 'Reino Unido',
    'lang.spain': 'Espanha',
    'lang.china': 'China',
    'lang.japan': 'Japão',
    'lang.thailand': 'Tailândia',
  },
  en: {
    // Header
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.skills': 'Skills',
    'nav.projects': 'Projects',
    'nav.experience': 'Experience',
    'nav.certifications': 'Certifications',
    'nav.contact': 'Contact',

    // Hero
    'hero.greeting': 'Hello, I am',
    'hero.title': 'Fullstack Developer',
    'hero.description': 'Specialist in web and mobile development with team leadership experience. Creator of innovative solutions for contact centers and business management.',
    'hero.fullstack': 'Full Stack',
    'hero.mobile': 'Mobile',
    'hero.devops': 'DevOps',
    'hero.viewProjects': 'View Projects',
    'hero.contact': 'Contact',

    // About
    'about.title': 'About',
    'about.subtitle': 'Learn more about my career and professional experience',
    'about.name': 'Name',
    'about.location': 'Location',
    'about.experience': 'Experience',
    'about.certification': 'Certification',
    'about.degree': 'Degree',
    'about.nameValue': 'Karim Hussen Patatas Hassam dos Santos',
    'about.locationValue': 'Golães, Fafe, Portugal',
    'about.experienceValue': '4+ years in development',
    'about.certificationValue': 'Oracle Certified Professional',
    'about.degreeValue': 'Bachelor in Computer Science',
    'about.currentRole': 'Current Role',
    'about.currentRoleValue': 'Fullstack Developer at Randstad Digital (Financial and Insurance Sector — Société Générale Assurances) — since August, GraphTalk AIA',
    'about.presentationTitle': 'Introduction',
    'about.presentation1': 'Software Engineer and Web/Mobile Developer with solid experience in programming, team management and development of innovative solutions for contact centers and business management.',
    'about.presentation2': 'Specialist in React Native and Mobile Computing, with advanced knowledge of Linux, Django, full stack web development, networks and systems integration.',
    'about.presentation3': 'Recognized for creativity, leadership skills and delivery of high-impact projects. Seeking a challenging position in Europe to contribute with cutting-edge technological solutions and grow in dynamic international environments.',
    'about.fullStackDev': 'Full Stack Developer',
    'about.fullStackDesc': 'Specialist in React, Django, React Native and mobile development',
    'about.ceoLeader': 'CEO & Technical Leader',
    'about.ceoDesc': 'Management of teams up to 50 employees and development of business solutions',
    'about.techInnovation': 'Technological Innovation',
    'about.techDesc': 'Creation of integrated platforms for contact centers and business management',

    // Skills
    'skills.title': 'Skills',
    'skills.subtitle': 'Technologies and tools I master to create complete solutions',
    'skills.frontend': 'Frontend',
    'skills.backend': 'Backend',
    'skills.mobile': 'Mobile',
    'skills.database': 'Database',
    'skills.tools': 'Tools & DevOps',
    'skills.languages': 'Languages',
    'skills.proficiencyLevels': 'Proficiency Levels',
    'skills.native': 'Native: Mother tongue',
    'skills.c2': 'C2: Full proficiency',
    'skills.b2': 'B2: Independent user',
    'skills.a1': 'A1: Elementary user',

    // Projects
    'projects.title': 'Projects',
    'projects.subtitle': 'A selection of my most recent and impactful work',
    'projects.all': 'All',
    'projects.web': 'Web',
    'projects.mobile': 'Mobile',
    'projects.fullstack': 'Full Stack',
    'projects.games': 'Games',
    'projects.viewSite': 'View Site',
    'projects.code': 'Code',
    'projects.online': 'Online',
    'projects.development': 'In Development',
    'projects.completed': 'Completed',
    'projects.testing': 'Testing',
    'projects.mobileApps': 'Mobile Apps in Testing',
    'projects.memoriza': 'App for memorization techniques',
    'projects.pocketcv': 'Professional resume creation',

    // Experience
    'experience.title': 'Experience',
    'experience.subtitle': 'Professional career and academic background',
    'experience.professional': 'Professional Experience',
    'experience.education': 'Education',
    'experience.current': 'Current',
    'experience.technologies': 'Technologies:',
    'experience.achievements': 'Key Achievements:',
    'experience.careerSummary': 'Career Summary',
    'experience.totalExperience': 'Total Experience:',
    'experience.teamManaged': 'Team Managed:',
    'experience.projectsLed': 'Projects Led:',
    'experience.certifications': 'Certifications:',
    'experience.years': '10+ years',
    'experience.people': '50+ people',
    'experience.projects': '15+',
    'experience.oracle': 'Oracle OCP',
    'experience.inProgress': 'In progress',
    'experience.completed': 'Completed',

    // Certifications
    'certifications.title': 'Certifications',
    'certifications.subtitle': 'digitally verified professional certifications',
    'certifications.view': 'View',
    'certifications.rumos': 'Rumos Certifications',
    'certifications.cisco': 'Cisco Certifications',
    'certifications.coursera': 'Coursera Certifications',

    // Contact
    'contact.title': 'Contact',
    'contact.subtitle': "Let's work together! Get in touch to discuss opportunities and projects",
    'contact.info': 'Contact Information',
    'contact.email': 'Email',
    'contact.location': 'Location',
    'contact.availability': 'Availability',
    'contact.availabilityValue': 'Monday to Friday, 9am-6pm',
    'contact.connectWithMe': 'Connect With Me',
    'contact.availableForProjects': 'Available for Projects',
    'contact.availableDesc': 'Currently open to new opportunities in full-stack development, technical leadership and technology consulting.',
    'contact.sendMessage': 'Send Message',
    'contact.name': 'Name',
    'contact.subject': 'Subject',
    'contact.message': 'Message',
    'contact.namePlaceholder': 'Your name',
    'contact.emailPlaceholder': 'your@email.com',
    'contact.subjectPlaceholder': 'Message subject',
    'contact.messagePlaceholder': 'Describe your project or opportunity...',
    'contact.sending': 'Sending...',
    'contact.send': 'Send Message',
    'contact.responseTime': 'I usually respond within',
    'contact.hours': '24 hours',

    // Git Activity
    'git.title': 'GitActivity',
    'git.subtitle': 'Development activity and contributions on GitHub',
    'git.totalCommits': 'Total Commits',
    'git.repositories': 'Repositories',
    'git.contributions2024': '2024 Contributions',
    'git.activeDays': 'Active Days',
    'git.contributionsIn': 'Contributions in 2024',
    'git.contributions': 'contributions',
    'git.days': 'days',
    'git.recentCommits': 'Recent Commits',
    'git.viewOnGithub': 'View on GitHub',
    'git.less': 'Less',
    'git.more': 'More',

    // Language names
    'lang.portuguese': 'Portuguese',
    'lang.french': 'French',
    'lang.english': 'English',
    'lang.spanish': 'Spanish',
    'lang.chinese': 'Chinese',
    'lang.japanese': 'Japanese',
    'lang.thai': 'Thai',
    'lang.native': 'Native',
    'lang.portugal': 'Portugal',
    'lang.france': 'France',
    'lang.uk': 'United Kingdom',
    'lang.spain': 'Spain',
    'lang.china': 'China',
    'lang.japan': 'Japan',
    'lang.thailand': 'Thailand',
  },
  fr: {
    // Header
    'nav.home': 'Accueil',
    'nav.about': 'À propos',
    'nav.skills': 'Compétences',
    'nav.projects': 'Projets',
    'nav.experience': 'Expérience',
    'nav.certifications': 'Certifications',
    'nav.contact': 'Contact',

    // Hero
    'hero.greeting': 'Bonjour, je suis',
    'hero.title': 'Développeur Fullstack',
    'hero.description': 'Spécialiste en développement web et mobile avec expérience en leadership d\'équipes. Créateur de solutions innovantes pour centres d\'appels et gestion d\'entreprise.',
    'hero.fullstack': 'Full Stack',
    'hero.mobile': 'Mobile',
    'hero.devops': 'DevOps',
    'hero.viewProjects': 'Voir Projets',
    'hero.contact': 'Contact',

    // About
    'about.title': 'À propos',
    'about.subtitle': 'En savoir plus sur mon parcours et mon expérience professionnelle',
    'about.name': 'Nom',
    'about.location': 'Localisation',
    'about.experience': 'Expérience',
    'about.certification': 'Certification',
    'about.degree': 'Diplôme',
    'about.nameValue': 'Karim Hussen Patatas Hassam dos Santos',
    'about.locationValue': 'Golães, Fafe, Portugal',
    'about.experienceValue': '4+ ans en développement',
    'about.certificationValue': 'Oracle Certified Professional',
    'about.degreeValue': 'Licence en Informatique',
    'about.currentRole': 'Rôle actuel',
    'about.currentRoleValue': 'Développeur Fullstack chez Randstad Digital (Secteur financier et assurance — Société Générale Assurances) — depuis août, GraphTalk AIA',
    'about.presentationTitle': 'Présentation',
    'about.presentation1': 'Ingénieur Logiciel et Développeur Web/Mobile avec une solide expérience en programmation, gestion d\'équipes et développement de solutions innovantes pour centres d\'appels et gestion d\'entreprise.',
    'about.presentation2': 'Spécialiste en React Native et Informatique Mobile, avec une maîtrise avancée de Linux, Django, développement web full stack, réseaux et intégration de systèmes.',
    'about.presentation3': 'Reconnu pour la créativité, les compétences de leadership et la livraison de projets à fort impact. Je recherche un poste stimulant en Europe pour contribuer avec des solutions technologiques de pointe et grandir dans des environnements internationaux dynamiques.',
    'about.fullStackDev': 'Développeur Full Stack',
    'about.fullStackDesc': 'Spécialiste en React, Django, React Native et développement mobile',
    'about.ceoLeader': 'PDG & Leader Technique',
    'about.ceoDesc': 'Gestion d\'équipes jusqu\'à 50 employés et développement de solutions d\'entreprise',
    'about.techInnovation': 'Innovation Technologique',
    'about.techDesc': 'Création de plateformes intégrées pour centres d\'appels et gestion d\'entreprise',

    // Skills
    'skills.title': 'Compétences',
    'skills.subtitle': 'Technologies et outils que je maîtrise pour créer des solutions complètes',
    'skills.frontend': 'Frontend',
    'skills.backend': 'Backend',
    'skills.mobile': 'Mobile',
    'skills.database': 'Base de données',
    'skills.tools': 'Outils & DevOps',
    'skills.languages': 'Langues',
    'skills.proficiencyLevels': 'Niveaux de Compétence',
    'skills.native': 'Natif: Langue maternelle',
    'skills.c2': 'C2: Maîtrise complète',
    'skills.b2': 'B2: Utilisateur indépendant',
    'skills.a1': 'A1: Utilisateur élémentaire',

    // Projects
    'projects.title': 'Projets',
    'projects.subtitle': 'Une sélection de mes travaux les plus récents et impactants',
    'projects.all': 'Tous',
    'projects.web': 'Web',
    'projects.mobile': 'Mobile',
    'projects.fullstack': 'Full Stack',
    'projects.games': 'Jeux',
    'projects.viewSite': 'Voir Site',
    'projects.code': 'Code',
    'projects.online': 'En ligne',
    'projects.completed': 'Terminé',
    'projects.development': 'En Développement',
    'projects.testing': 'En Test',
    'projects.mobileApps': 'Apps Mobile en Test',
    'projects.memoriza': 'App pour techniques de mémorisation',
    'projects.pocketcv': 'Création de CV professionnels',

    // Experience
    'experience.title': 'Expérience',
    'experience.subtitle': 'Parcours professionnel et formation académique',
    'experience.professional': 'Expérience Professionnelle',
    'experience.education': 'Formation',
    'experience.current': 'Actuel',
    'experience.technologies': 'Technologies:',
    'experience.achievements': 'Principales Réalisations:',
    'experience.careerSummary': 'Résumé de Carrière',
    'experience.totalExperience': 'Expérience Totale:',
    'experience.teamManaged': 'Équipe Gérée:',
    'experience.projectsLed': 'Projets Dirigés:',
    'experience.certifications': 'Certifications:',
    'experience.years': '10+ ans',
    'experience.people': '50+ personnes',
    'experience.projects': '15+',
    'experience.oracle': 'Oracle OCP',
    'experience.inProgress': 'En cours',
    'experience.completed': 'Terminé',

    // Certifications
    'certifications.title': 'Certifications',
    'certifications.subtitle': 'certifications professionnelles vérifiées numériquement',
    'certifications.view': 'Voir',
    'certifications.rumos': 'Certifications Rumos',
    'certifications.cisco': 'Certifications Cisco',
    'certifications.coursera': 'Certifications Coursera',

    // Contact
    'contact.title': 'Contact',
    'contact.subtitle': 'Travaillons ensemble! Contactez-moi pour discuter d\'opportunités et de projets',
    'contact.info': 'Informations de Contact',
    'contact.email': 'Email',
    'contact.location': 'Localisation',
    'contact.availability': 'Disponibilité',
    'contact.availabilityValue': 'Lundi au Vendredi, 9h-18h',
    'contact.connectWithMe': 'Connectez-vous avec Moi',
    'contact.availableForProjects': 'Disponible pour Projets',
    'contact.availableDesc': 'Actuellement ouvert à de nouvelles opportunités en développement full-stack, leadership technique et conseil en technologie.',
    'contact.sendMessage': 'Envoyer Message',
    'contact.name': 'Nom',
    'contact.subject': 'Sujet',
    'contact.message': 'Message',
    'contact.namePlaceholder': 'Votre nom',
    'contact.emailPlaceholder': 'votre@email.com',
    'contact.subjectPlaceholder': 'Sujet du message',
    'contact.messagePlaceholder': 'Décrivez votre projet ou opportunité...',
    'contact.sending': 'Envoi...',
    'contact.send': 'Envoyer Message',
    'contact.responseTime': 'Je réponds normalement dans',
    'contact.hours': '24 heures',

    // Git Activity
    'git.title': 'Activité Git',
    'git.subtitle': 'Activité de développement et contributions sur GitHub',
    'git.totalCommits': 'Total Commits',
    'git.repositories': 'Dépôts',
    'git.contributions2024': 'Contributions 2024',
    'git.activeDays': 'Jours Actifs',
    'git.contributionsIn': 'Contributions en 2024',
    'git.contributions': 'contributions',
    'git.days': 'jours',
    'git.recentCommits': 'Commits Récents',
    'git.viewOnGithub': 'Voir sur GitHub',
    'git.less': 'Moins',
    'git.more': 'Plus',

    // Language names
    'lang.portuguese': 'Portugais',
    'lang.french': 'Français',
    'lang.english': 'Anglais',
    'lang.spanish': 'Espagnol',
    'lang.chinese': 'Chinois',
    'lang.japanese': 'Japonais',
    'lang.thai': 'Thaï',
    'lang.native': 'Natif',
    'lang.portugal': 'Portugal',
    'lang.france': 'France',
    'lang.uk': 'Royaume-Uni',
    'lang.spain': 'Espagne',
    'lang.china': 'Chine',
    'lang.japan': 'Japon',
    'lang.thailand': 'Thaïlande',
  },
  es: {
    // Header
    'nav.home': 'Inicio',
    'nav.about': 'Acerca de',
    'nav.skills': 'Habilidades',
    'nav.projects': 'Proyectos',
    'nav.experience': 'Experiencia',
    'nav.certifications': 'Certificaciones',
    'nav.contact': 'Contacto',

    // Hero
    'hero.greeting': 'Hola, soy',
    'hero.title': 'Desarrollador Fullstack',
    'hero.description': 'Especialista en desarrollo web y móvil con experiencia en liderazgo de equipos. Creador de soluciones innovadoras para centros de contacto y gestión empresarial.',
    'hero.fullstack': 'Full Stack',
    'hero.mobile': 'Móvil',
    'hero.devops': 'DevOps',
    'hero.viewProjects': 'Ver Proyectos',
    'hero.contact': 'Contactar',

    // About
    'about.title': 'Acerca de',
    'about.subtitle': 'Conoce más sobre mi trayectoria y experiencia profesional',
    'about.name': 'Nombre',
    'about.location': 'Ubicación',
    'about.experience': 'Experiencia',
    'about.certification': 'Certificación',
    'about.degree': 'Título',
    'about.nameValue': 'Karim Hussen Patatas Hassam dos Santos',
    'about.locationValue': 'Golães, Fafe, Portugal',
    'about.experienceValue': '4+ años en desarrollo',
    'about.certificationValue': 'Oracle Certified Professional',
    'about.degreeValue': 'Licenciado en Informática',
    'about.currentRole': 'Rol actual',
    'about.currentRoleValue': 'Desarrollador Fullstack en Randstad Digital (Sector financiero y asegurador — Société Générale Assurances) — desde agosto, GraphTalk AIA',
    'about.presentationTitle': 'Presentación',
    'about.presentation1': 'Ingeniero de Software y Desarrollador Web/Móvil con sólida experiencia en programación, gestión de equipos y desarrollo de soluciones innovadoras para centros de contacto y gestión empresarial.',
    'about.presentation2': 'Especialista en React Native y Computación Móvil, con dominio avanzado de Linux, Django, desarrollo web full stack, redes e integración de sistemas.',
    'about.presentation3': 'Reconocido por la creatividad, capacidad de liderazgo y entrega de proyectos de alto impacto. Busco una posición desafiante en Europa para contribuir con soluciones tecnológicas de vanguardia y crecer en entornos internacionales dinámicos.',
    'about.fullStackDev': 'Desarrollador Full Stack',
    'about.fullStackDesc': 'Especialista en React, Django, React Native y desarrollo móvil',
    'about.ceoLeader': 'CEO & Líder Técnico',
    'about.ceoDesc': 'Gestión de equipos de hasta 50 empleados y desarrollo de soluciones empresariales',
    'about.techInnovation': 'Innovación Tecnológica',
    'about.techDesc': 'Creación de plataformas integradas para centros de contacto y gestión empresarial',

    // Skills
    'skills.title': 'Habilidades',
    'skills.subtitle': 'Tecnologías y herramientas que domino para crear soluciones completas',
    'skills.frontend': 'Frontend',
    'skills.backend': 'Backend',
    'skills.mobile': 'Móvil',
    'skills.database': 'Base de datos',
    'skills.tools': 'Herramientas & DevOps',
    'skills.languages': 'Idiomas',
    'skills.proficiencyLevels': 'Niveles de Competencia',
    'skills.native': 'Nativo: Lengua materna',
    'skills.c2': 'C2: Dominio completo',
    'skills.b2': 'B2: Usuario independiente',
    'skills.a1': 'A1: Usuario básico',

    // Projects
    'projects.title': 'Proyectos',
    'projects.subtitle': 'Una selección de mis trabajos más recientes e impactantes',
    'projects.all': 'Todos',
    'projects.web': 'Web',
    'projects.mobile': 'Móvil',
    'projects.fullstack': 'Full Stack',
    'projects.games': 'Juegos',
    'projects.viewSite': 'Ver Sitio',
    'projects.code': 'Código',
    'projects.online': 'En línea',
    'projects.completed': 'Completado',
    'projects.development': 'En Desarrollo',
    'projects.testing': 'En Pruebas',
    'projects.mobileApps': 'Apps Móviles en Pruebas',
    'projects.memoriza': 'App para técnicas de memorización',
    'projects.pocketcv': 'Creación de currículos profesionales',

    // Experience
    'experience.title': 'Experiencia',
    'experience.subtitle': 'Trayectoria profesional y formación académica',
    'experience.professional': 'Experiencia Profesional',
    'experience.education': 'Formación',
    'experience.current': 'Actual',
    'experience.technologies': 'Tecnologías:',
    'experience.achievements': 'Principales Logros:',
    'experience.careerSummary': 'Resumen de Carrera',
    'experience.totalExperience': 'Experiencia Total:',
    'experience.teamManaged': 'Equipo Gestionado:',
    'experience.projectsLed': 'Proyectos Liderados:',
    'experience.certifications': 'Certificaciones:',
    'experience.years': '10+ años',
    'experience.people': '50+ personas',
    'experience.projects': '15+',
    'experience.oracle': 'Oracle OCP',
    'experience.inProgress': 'En progreso',
    'experience.completed': 'Completado',

    // Certifications
    'certifications.title': 'Certificaciones',
    'certifications.subtitle': 'certificaciones profesionales verificadas digitalmente',
    'certifications.view': 'Ver',
    'certifications.rumos': 'Certificaciones Rumos',
    'certifications.cisco': 'Certificaciones Cisco',
    'certifications.coursera': 'Certificaciones Coursera',

    // Contact
    'contact.title': 'Contacto',
    'contact.subtitle': '¡Trabajemos juntos! Ponte en contacto para discutir oportunidades y proyectos',
    'contact.info': 'Información de Contacto',
    'contact.email': 'Email',
    'contact.location': 'Ubicación',
    'contact.availability': 'Disponibilidad',
    'contact.availabilityValue': 'Lunes a Viernes, 9h-18h',
    'contact.connectWithMe': 'Conéctate Conmigo',
    'contact.availableForProjects': 'Disponible para Proyectos',
    'contact.availableDesc': 'Actualmente abierto a nuevas oportunidades en desarrollo full-stack, liderazgo técnico y consultoría tecnológica.',
    'contact.sendMessage': 'Enviar Mensaje',
    'contact.name': 'Nombre',
    'contact.subject': 'Asunto',
    'contact.message': 'Mensaje',
    'contact.namePlaceholder': 'Tu nombre',
    'contact.emailPlaceholder': 'tu@email.com',
    'contact.subjectPlaceholder': 'Asunto del mensaje',
    'contact.messagePlaceholder': 'Describe tu proyecto u oportunidad...',
    'contact.sending': 'Enviando...',
    'contact.send': 'Enviar Mensaje',
    'contact.responseTime': 'Normalmente respondo en',
    'contact.hours': '24 horas',

    // Git Activity
    'git.title': 'Actividad Git',
    'git.subtitle': 'Actividad de desarrollo y contribuciones en GitHub',
    'git.totalCommits': 'Total Commits',
    'git.repositories': 'Repositorios',
    'git.contributions2024': 'Contribuciones 2024',
    'git.activeDays': 'Días Activos',
    'git.contributionsIn': 'Contribuciones en 2024',
    'git.contributions': 'contribuciones',
    'git.days': 'días',
    'git.recentCommits': 'Commits Recientes',
    'git.viewOnGithub': 'Ver en GitHub',
    'git.less': 'Menos',
    'git.more': 'Más',

    // Language names
    'lang.portuguese': 'Portugués',
    'lang.french': 'Francés',
    'lang.english': 'Inglés',
    'lang.spanish': 'Español',
    'lang.chinese': 'Chino',
    'lang.japanese': 'Japonés',
    'lang.thai': 'Tailandés',
    'lang.native': 'Nativo',
    'lang.portugal': 'Portugal',
    'lang.france': 'Francia',
    'lang.uk': 'Reino Unido',
    'lang.spain': 'España',
    'lang.china': 'China',
    'lang.japan': 'Japón',
    'lang.thailand': 'Tailandia',
  },
  de: {
    // Header
    'nav.home': 'Startseite',
    'nav.about': 'Über mich',
    'nav.skills': 'Fähigkeiten',
    'nav.projects': 'Projekte',
    'nav.experience': 'Erfahrung',
    'nav.certifications': 'Zertifikate',
    'nav.contact': 'Kontakt',

    // Hero
    'hero.greeting': 'Hallo, ich bin',
    'hero.title': 'Full Stack Entwickler',
    'hero.description': 'Spezialist für Web- und Mobile-Entwicklung mit Erfahrung in Teamführung. Entwickler innovativer Lösungen für Contact Center und Unternehmensführung.',
    'hero.fullstack': 'Full Stack',
    'hero.mobile': 'Mobile',
    'hero.devops': 'DevOps',
    'hero.viewProjects': 'Projekte anzeigen',
    'hero.contact': 'Kontakt',

    // About
    'about.title': 'Über mich',
    'about.subtitle': 'Erfahren Sie mehr über meinen Werdegang und meine Berufserfahrung',
    'about.name': 'Name',
    'about.location': 'Standort',
    'about.experience': 'Erfahrung',
    'about.certification': 'Zertifizierung',
    'about.degree': 'Abschluss',
    'about.nameValue': 'Karim Hussen Patatas Hassam dos Santos',
    'about.locationValue': 'Golães, Fafe, Portugal',
    'about.experienceValue': '4+ Jahre in der Entwicklung',
    'about.certificationValue': 'Oracle Certified Professional',
    'about.degreeValue': 'Bachelor in Informatik',
    'about.currentRole': 'Aktuelle Rolle',
    'about.currentRoleValue': 'Fullstack-Entwickler bei Randstad Digital (Finanz- und Versicherungssektor — Société Générale Assurances) — seit August, GraphTalk AIA',
    'about.presentationTitle': 'Vorstellung',
    'about.presentation1': 'Software-Ingenieur und Web-/Mobile-Entwickler mit solider Erfahrung in Programmierung, Teammanagement und Entwicklung innovativer Lösungen für Contact Center und Unternehmensführung.',
    'about.presentation2': 'Spezialist für React Native und Mobile Computing mit fortgeschrittenen Kenntnissen in Linux, Django, Full-Stack-Webentwicklung, Netzwerken und Systemintegration.',
    'about.presentation3': 'Anerkannt für Kreativität, Führungsqualitäten und die Umsetzung von Projekten mit hoher Wirkung. Ich suche eine herausfordernde Position in Europa, um mit modernsten technologischen Lösungen zu beitragen und in dynamischen internationalen Umgebungen zu wachsen.',
    'about.fullStackDev': 'Full Stack Entwickler',
    'about.fullStackDesc': 'Spezialist für React, Django, React Native und Mobile-Entwicklung',
    'about.ceoLeader': 'CEO & Technischer Leiter',
    'about.ceoDesc': 'Management von Teams mit bis zu 50 Mitarbeitern und Entwicklung von Unternehmenslösungen',
    'about.techInnovation': 'Technologische Innovation',
    'about.techDesc': 'Erstellung integrierter Plattformen für Contact Center und Unternehmensführung',

    // Skills
    'skills.title': 'Fähigkeiten',
    'skills.subtitle': 'Technologien und Tools, die ich beherrsche, um vollständige Lösungen zu erstellen',
    'skills.frontend': 'Frontend',
    'skills.backend': 'Backend',
    'skills.mobile': 'Mobile',
    'skills.database': 'Datenbank',
    'skills.tools': 'Tools & DevOps',
    'skills.languages': 'Sprachen',
    'skills.proficiencyLevels': 'Kompetenzniveaus',
    'skills.native': 'Muttersprache',
    'skills.c2': 'C2: Vollständige Beherrschung',
    'skills.b2': 'B2: Selbstständiger Benutzer',
    'skills.a1': 'A1: Grundlegender Benutzer',

    // Projects
    'projects.title': 'Projekte',
    'projects.subtitle': 'Eine Auswahl meiner neuesten und wirkungsvollsten Arbeiten',
    'projects.all': 'Alle',
    'projects.web': 'Web',
    'projects.mobile': 'Mobile',
    'projects.fullstack': 'Full Stack',
    'projects.games': 'Spiele',
    'projects.viewSite': 'Website anzeigen',
    'projects.code': 'Code',
    'projects.online': 'Online',
    'projects.completed': 'Abgeschlossen',
    'projects.development': 'In Entwicklung',
    'projects.testing': 'Im Test',
    'projects.mobileApps': 'Mobile Apps im Test',
    'projects.memoriza': 'App für Gedächtnistechniken',
    'projects.pocketcv': 'Professionelle Lebenslauf-Erstellung',

    // Experience
    'experience.title': 'Erfahrung',
    'experience.subtitle': 'Berufslaufbahn und akademischer Hintergrund',
    'experience.professional': 'Berufserfahrung',
    'experience.education': 'Bildung',
    'experience.current': 'Aktuell',
    'experience.technologies': 'Technologien:',
    'experience.achievements': 'Haupterfolge:',
    'experience.careerSummary': 'Karrierezusammenfassung',
    'experience.totalExperience': 'Gesamterfahrung:',
    'experience.teamManaged': 'Verwaltetes Team:',
    'experience.projectsLed': 'Geleitete Projekte:',
    'experience.certifications': 'Zertifizierungen:',
    'experience.years': '10+ Jahre',
    'experience.people': '50+ Personen',
    'experience.projects': '15+',
    'experience.oracle': 'Oracle OCP',
    'experience.inProgress': 'In Bearbeitung',
    'experience.completed': 'Abgeschlossen',

    // Certifications
    'certifications.title': 'Zertifizierungen',
    'certifications.subtitle': 'digital verifizierte professionelle Zertifizierungen',
    'certifications.view': 'Anzeigen',
    'certifications.rumos': 'Rumos Zertifizierungen',
    'certifications.cisco': 'Cisco Zertifizierungen',
    'certifications.coursera': 'Coursera Zertifizierungen',

    // Contact
    'contact.title': 'Kontakt',
    'contact.subtitle': 'Lassen Sie uns zusammenarbeiten! Kontaktieren Sie mich, um Möglichkeiten und Projekte zu besprechen',
    'contact.info': 'Kontaktinformationen',
    'contact.email': 'E-Mail',
    'contact.location': 'Standort',
    'contact.availability': 'Verfügbarkeit',
    'contact.availabilityValue': 'Montag bis Freitag, 9-18 Uhr',
    'contact.connectWithMe': 'Verbinden Sie sich mit mir',
    'contact.availableForProjects': 'Verfügbar für Projekte',
    'contact.availableDesc': 'Derzeit offen für neue Möglichkeiten in Full-Stack-Entwicklung, technischer Führung und Technologieberatung.',
    'contact.sendMessage': 'Nachricht senden',
    'contact.name': 'Name',
    'contact.subject': 'Betreff',
    'contact.message': 'Nachricht',
    'contact.namePlaceholder': 'Ihr Name',
    'contact.emailPlaceholder': 'ihre@email.com',
    'contact.subjectPlaceholder': 'Nachrichtenbetreff',
    'contact.messagePlaceholder': 'Beschreiben Sie Ihr Projekt oder Ihre Gelegenheit...',
    'contact.sending': 'Senden...',
    'contact.send': 'Nachricht senden',
    'contact.responseTime': 'Ich antworte normalerweise innerhalb von',
    'contact.hours': '24 Stunden',

    // Git Activity
    'git.title': 'Git-Aktivität',
    'git.subtitle': 'Entwicklungsaktivität und Beiträge auf GitHub',
    'git.totalCommits': 'Gesamt Commits',
    'git.repositories': 'Repositories',
    'git.contributions2024': 'Beiträge 2024',
    'git.activeDays': 'Aktive Tage',
    'git.contributionsIn': 'Beiträge in 2024',
    'git.contributions': 'Beiträge',
    'git.days': 'Tage',
    'git.recentCommits': 'Neueste Commits',
    'git.viewOnGithub': 'Auf GitHub anzeigen',
    'git.less': 'Weniger',
    'git.more': 'Mehr',

    // Language names
    'lang.portuguese': 'Portugiesisch',
    'lang.french': 'Französisch',
    'lang.english': 'Englisch',
    'lang.spanish': 'Spanisch',
    'lang.chinese': 'Chinesisch',
    'lang.japanese': 'Japanisch',
    'lang.thai': 'Thailändisch',
    'lang.native': 'Muttersprache',
    'lang.portugal': 'Portugal',
    'lang.france': 'Frankreich',
    'lang.uk': 'Vereinigtes Königreich',
    'lang.spain': 'Spanien',
    'lang.china': 'China',
    'lang.japan': 'Japan',
    'lang.thailand': 'Thailand',
  },
  it: {
    // Header
    'nav.home': 'Home',
    'nav.about': 'Chi sono',
    'nav.skills': 'Competenze',
    'nav.projects': 'Progetti',
    'nav.experience': 'Esperienza',
    'nav.certifications': 'Certificazioni',
    'nav.contact': 'Contatto',

    // Hero
    'hero.greeting': 'Ciao, sono',
    'hero.title': 'Sviluppatore Full Stack',
    'hero.description': 'Specialista nello sviluppo web e mobile con esperienza nella leadership di team. Creatore di soluzioni innovative per contact center e gestione aziendale.',
    'hero.fullstack': 'Full Stack',
    'hero.mobile': 'Mobile',
    'hero.devops': 'DevOps',
    'hero.viewProjects': 'Vedi Progetti',
    'hero.contact': 'Contatto',

    // About
    'about.title': 'Chi sono',
    'about.subtitle': 'Scopri di più sul mio percorso e la mia esperienza professionale',
    'about.name': 'Nome',
    'about.location': 'Posizione',
    'about.experience': 'Esperienza',
    'about.certification': 'Certificazione',
    'about.degree': 'Laurea',
    'about.nameValue': 'Karim Hussen Patatas Hassam dos Santos',
    'about.locationValue': 'Golães, Fafe, Portogallo',
    'about.experienceValue': '4+ anni nello sviluppo',
    'about.certificationValue': 'Oracle Certified Professional',
    'about.degreeValue': 'Laureato in Informatica',
    'about.currentRole': 'Ruolo Attuale',
    'about.currentRoleValue': 'Sviluppatore Fullstack presso Randstad Digital (Settore finanziario e assicurativo — Société Générale Assurances) — da agosto, GraphTalk AIA',
    'about.presentationTitle': 'Presentazione',
    'about.presentation1': 'Ingegnere Software e Sviluppatore Web/Mobile con solida esperienza in programmazione, gestione di team e sviluppo di soluzioni innovative per contact center e gestione aziendale.',
    'about.presentation2': 'Specialista in React Native e Informatica Mobile, con padronanza avanzata di Linux, Django, sviluppo web full stack, reti e integrazione di sistemi.',
    'about.presentation3': 'Riconosciuto per creatività, capacità di leadership e consegna di progetti ad alto impatto. Cerco una posizione stimolante in Europa per contribuire con soluzioni tecnologiche all\'avanguardia e crescere in ambienti internazionali dinamici.',
    'about.fullStackDev': 'Sviluppatore Full Stack',
    'about.fullStackDesc': 'Specialista in React, Django, React Native e sviluppo mobile',
    'about.ceoLeader': 'CEO & Leader Tecnico',
    'about.ceoDesc': 'Gestione di team fino a 50 dipendenti e sviluppo di soluzioni aziendali',
    'about.techInnovation': 'Innovazione Tecnologica',
    'about.techDesc': 'Creazione di piattaforme integrate per contact center e gestione aziendale',

    // Skills
    'skills.title': 'Competenze',
    'skills.subtitle': 'Tecnologie e strumenti che padroneggio per creare soluzioni complete',
    'skills.frontend': 'Frontend',
    'skills.backend': 'Backend',
    'skills.mobile': 'Mobile',
    'skills.database': 'Database',
    'skills.tools': 'Strumenti & DevOps',
    'skills.languages': 'Lingue',
    'skills.proficiencyLevels': 'Livelli di Competenza',
    'skills.native': 'Madrelingua',
    'skills.c2': 'C2: Padronanza completa',
    'skills.b2': 'B2: Utente indipendente',
    'skills.a1': 'A1: Utente elementare',

    // Projects
    'projects.title': 'Progetti',
    'projects.subtitle': 'Una selezione dei miei lavori più recenti e d\'impatto',
    'projects.all': 'Tutti',
    'projects.web': 'Web',
    'projects.mobile': 'Mobile',
    'projects.fullstack': 'Full Stack',
    'projects.games': 'Giochi',
    'projects.viewSite': 'Vedi Sito',
    'projects.code': 'Codice',
    'projects.online': 'Online',
    'projects.completed': 'Completato',
    'projects.development': 'In Sviluppo',
    'projects.testing': 'In Test',
    'projects.mobileApps': 'App Mobile in Test',
    'projects.memoriza': 'App per tecniche di memorizzazione',
    'projects.pocketcv': 'Creazione di CV professionali',

    // Experience
    'experience.title': 'Esperienza',
    'experience.subtitle': 'Percorso professionale e formazione accademica',
    'experience.professional': 'Esperienza Professionale',
    'experience.education': 'Formazione',
    'experience.current': 'Attuale',
    'experience.technologies': 'Tecnologie:',
    'experience.achievements': 'Principali Risultati:',
    'experience.careerSummary': 'Riassunto Carriera',
    'experience.totalExperience': 'Esperienza Totale:',
    'experience.teamManaged': 'Team Gestito:',
    'experience.projectsLed': 'Progetti Guidati:',
    'experience.certifications': 'Certificazioni:',
    'experience.years': '10+ anni',
    'experience.people': '50+ persone',
    'experience.projects': '15+',
    'experience.oracle': 'Oracle OCP',
    'experience.inProgress': 'In corso',
    'experience.completed': 'Completato',

    // Certifications
    'certifications.title': 'Certificazioni',
    'certifications.subtitle': 'certificazioni professionali verificate digitalmente',
    'certifications.view': 'Visualizza',
    'certifications.rumos': 'Certificazioni Rumos',
    'certifications.cisco': 'Certificazioni Cisco',
    'certifications.coursera': 'Certificazioni Coursera',

    // Contact
    'contact.title': 'Contatto',
    'contact.subtitle': 'Lavoriamo insieme! Contattami per discutere opportunità e progetti',
    'contact.info': 'Informazioni di Contatto',
    'contact.email': 'Email',
    'contact.location': 'Posizione',
    'contact.availability': 'Disponibilità',
    'contact.availabilityValue': 'Lunedì al Venerdì, 9-18',
    'contact.connectWithMe': 'Connettiti con Me',
    'contact.availableForProjects': 'Disponibile per Progetti',
    'contact.availableDesc': 'Attualmente aperto a nuove opportunità nello sviluppo full-stack, leadership tecnica e consulenza tecnologica.',
    'contact.sendMessage': 'Invia Messaggio',
    'contact.name': 'Nome',
    'contact.subject': 'Oggetto',
    'contact.message': 'Messaggio',
    'contact.namePlaceholder': 'Il tuo nome',
    'contact.emailPlaceholder': 'tua@email.com',
    'contact.subjectPlaceholder': 'Oggetto del messaggio',
    'contact.messagePlaceholder': 'Descrivi il tuo progetto o opportunità...',
    'contact.sending': 'Invio...',
    'contact.send': 'Invia Messaggio',
    'contact.responseTime': 'Rispondo normalmente entro',
    'contact.hours': '24 ore',

    // Git Activity
    'git.title': 'Attività Git',
    'git.subtitle': 'Attività di sviluppo e contributi su GitHub',
    'git.totalCommits': 'Commit Totali',
    'git.repositories': 'Repository',
    'git.contributions2024': 'Contributi 2024',
    'git.activeDays': 'Giorni Attivi',
    'git.contributionsIn': 'Contributi nel 2024',
    'git.contributions': 'contributi',
    'git.days': 'giorni',
    'git.recentCommits': 'Commit Recenti',
    'git.viewOnGithub': 'Visualizza su GitHub',
    'git.less': 'Meno',
    'git.more': 'Più',

    // Language names
    'lang.portuguese': 'Portoghese',
    'lang.french': 'Francese',
    'lang.english': 'Inglese',
    'lang.spanish': 'Spagnolo',
    'lang.chinese': 'Cinese',
    'lang.japanese': 'Giapponese',
    'lang.thai': 'Tailandese',
    'lang.native': 'Madrelingua',
    'lang.portugal': 'Portogallo',
    'lang.france': 'Francia',
    'lang.uk': 'Regno Unito',
    'lang.spain': 'Spagna',
    'lang.china': 'Cina',
    'lang.japan': 'Giappone',
    'lang.thailand': 'Tailandia',
  }
};
